// useDataSource — Phase 5 D-Live Slice 3.
//
// React hook that loads the dashboard's seven UI lists from the
// configured DataSource (production: ApiDataSource hitting the
// app process; dev: FixtureDataSource). Returns the parallel
// arrays + a manual `refresh()` callback for screens that need
// to re-fetch after a mutating action (confirm an approval,
// queue a config change, etc.).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createDataSource,
  type AutoApprovalsConfig,
  type CryptoSummary,
  type CryptoValidationRun,
  type ForexAccount,
  type ForexConfig,
  type ForexPair,
  type DataSource,
  type GeneratedSignal,
  type GenerateSignalsResult,
  type ResearchReportCreateResult,
  type ResearchReportRow,
  type SignalHistoryRow,
} from "./dataSource";
import type {
  AiCostStatusWire,
  ApprovalReviewResponse,
  ConfirmAndPlaceResponse,
} from "./client";
import type {
  Account,
  Basket,
  ChartSnapshot,
  ChartTimeframe,
  DecisionReflection,
  EvaluationResult,
  EventRow,
  Investment,
  MultiAgentReviewResult,
  Pending,
  PendingConfigChange,
  Position,
  PositionReassessment,
  PositionTrade,
  RiskLimit,
  RunAgentReviewResult,
  SavedResearchEvaluation,
  SectorLimits,
  SectorLimitsUpdate,
  SystemHealthRow,
  TradePreview,
} from "../types";

export type DataSourceState = {
  accounts: Account[];
  pending: Pending[];
  positions: Position[];
  positionReassessments: PositionReassessment[];
  positionTrades: PositionTrade[];
  investments: Investment[];
  baskets: Basket[];
  riskLimits: RiskLimit[];
  pendingChanges: PendingConfigChange[];
  autoApprovalsConfig: AutoApprovalsConfig | null;
  aiCostStatus: AiCostStatusWire | null;
  // Phase 5 (d): Dashboard System tile + Recent Events panel.
  systemHealth: SystemHealthRow[];
  recentEvents: EventRow[];
  // Phase 8: Dashboard crypto tile. null until the first load completes
  // (or if both crypto endpoints fail).
  cryptoSummary: CryptoSummary | null;
  cryptoValidationRuns: CryptoValidationRun[];
  // Phase 9-UI: forex gate + pair reference (ForexScreen reads).
  forexConfig: ForexConfig | null;
  forexPairs: ForexPair[];
  // Phase 9 setup: tastyfx demo-row registration state (null until
  // the first load completes or when the read degrades).
  forexAccount: ForexAccount | null;
  isLoading: boolean;
  // Best-effort first-load error. The dashboard renders empty
  // lists when this is set; App.tsx surfaces it as a persistent
  // stale-data banner (2026-06-10 safety review, finding 7).
  error: string | null;
  // Wall-clock time of the last refresh() that completed without a
  // hard error. null until the first successful load. Lets the
  // stale-data banner say how old the displayed data is.
  lastSuccessAt: Date | null;
  // Labels of optional reads that failed (and fell back) during the
  // most recent refresh. Empty when everything loaded.
  degradedReads: string[];
  // Re-fetch all lists. Returns a Promise so callers can await it
  // before re-rendering dependent state.
  refresh: () => Promise<void>;
  // Operator-initiated signal run. Returns the generation summary
  // so callers can show inline feedback. Does NOT auto-refresh —
  // callers should call refresh() after if they want the pending
  // list to update.
  generateSignals: () => Promise<GenerateSignalsResult>;
  resumeBatchResearchEnrichment: DataSource["resumeBatchResearchEnrichment"];
  listCryptoValidationRuns: DataSource["listCryptoValidationRuns"];
  listMoonshotSignals: DataSource["listMoonshotSignals"];
  rankMoonshotTopTen: DataSource["rankMoonshotTopTen"];
  listPositionReassessments: DataSource["listPositionReassessments"];
  runPositionReassessment: DataSource["runPositionReassessment"];
  confirmPositionReassessmentAction: DataSource["confirmPositionReassessmentAction"];
  dismissPositionReassessment: DataSource["dismissPositionReassessment"];
  listRiskLimits: DataSource["listRiskLimits"];
  getRiskSnapshot: DataSource["getRiskSnapshot"];
  getSectorLimits: (brokerAccountId: string) => Promise<SectorLimits>;
  setSectorLimits: (args: SectorLimitsUpdate) => Promise<SectorLimits>;
  setAutoApprovalsConfig: (
    args: { enabled: boolean; minConfidence: number },
  ) => Promise<AutoApprovalsConfig>;
  listStrategyRoutes: DataSource["listStrategyRoutes"];
  updateStrategyRoute: DataSource["updateStrategyRoute"];
  listSignalHistory: (limit?: number) => Promise<SignalHistoryRow[]>;
  listResearchReports: (
    limit?: number,
    symbol?: string,
  ) => Promise<ResearchReportRow[]>;
  createResearchReport: (args: {
    symbol: string;
    template?: "research_v1" | "deep_dive_v1";
    forceRegenerate?: boolean;
  }) => Promise<ResearchReportCreateResult>;
  listSavedResearchEvaluations: (
    limit?: number,
    symbol?: string,
  ) => Promise<SavedResearchEvaluation[]>;
  evaluateSymbol: (
    symbol: string,
    horizon: "day_swing" | "investment" | "both",
    asOf?: string,
  ) => Promise<EvaluationResult>;
  acceptEvaluation: (evaluationId: string) => Promise<void>;
  createApprovalFromEvaluation: (args: {
    evaluationId: string;
    brokerAccountId: string;
    sizedShares: string;
    accountEquitySnapshot: string;
  }) => Promise<{ approvalId: string; symbol: string }>;
  runAgentReview: (
    evaluationId: string,
    includeSocialSentiment: boolean,
    resumeWorkflowRunId?: string,
  ) => Promise<RunAgentReviewResult>;
  getAgentReview: (evaluationId: string) => Promise<MultiAgentReviewResult>;
  runShadowAgentReviews: DataSource["runShadowAgentReviews"];
  getWorkflowStatus: DataSource["getWorkflowStatus"];
  getLatestWorkflowStatus: DataSource["getLatestWorkflowStatus"];
  listDecisionReflections: (
    limit?: number,
    symbol?: string,
  ) => Promise<DecisionReflection[]>;
  generateDecisionReflections: DataSource["generateDecisionReflections"];
  repriceSignalApproval: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<string>;
  previewSignalApproval: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<{
    preview: TradePreview;
    actionable: boolean;
    issues: string[];
  }>;
  reviewApproval: (approvalId: string) => Promise<ApprovalReviewResponse>;
  resizeApproval: DataSource["resizeApproval"];
  haltAll: (reason?: string) => Promise<void>;
  resumeAll: () => Promise<void>;
  confirmAndPlaceApproval: (
    approvalId: string,
    idempotencyKey: string,
    liveRealMoneyAck?: boolean,
  ) => Promise<ConfirmAndPlaceResponse>;
  listClosedStockTrades: DataSource["listClosedStockTrades"];
  listChartSymbols: () => Promise<string[]>;
  getChartSnapshot: (
    symbol: string,
    lookback?: number,
    timeframe?: ChartTimeframe,
  ) => Promise<ChartSnapshot>;
  // Phase 5 (d) Open-Position Management D2 — pass-throughs to the
  // four position-action endpoints + live-P&L snapshot.
  getLivePnL: DataSource["getLivePnL"];
  tightenStop: DataSource["tightenStop"];
  adjustTarget: DataSource["adjustTarget"];
  breakEven: DataSource["breakEven"];
  exitAtMarket: DataSource["exitAtMarket"];
  exitAtLimit: DataSource["exitAtLimit"];
  adjustPositionQty: DataSource["adjustPositionQty"];
  // True when the underlying source is the FixtureDataSource
  // (for the "FIXTURE MODE" banner in the shell).
  usingFixtures: boolean;
};

const optionalRead = async <T,>(
  label: string,
  load: () => Promise<T>,
  fallback: T,
  failed?: string[],
): Promise<T> => {
  try {
    return await load();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[useDataSource] optional ${label} failed`, e);
    // Record the degradation so the shell can surface it instead of
    // silently rendering the fallback (2026-06-10 safety review,
    // finding 7).
    failed?.push(label);
    return fallback;
  }
};

export function useDataSource(): DataSourceState {
  // createDataSource is async (Phase 5 D-Live Slice 5: dynamic
  // import for fixture mode keeps fixtures out of prod bundles).
  // We hold the resolved source in state and short-circuit
  // refreshes until it lands.
  const [source, setSource] = useState<DataSource | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [positionReassessments, setPositionReassessments] = useState<
    PositionReassessment[]
  >([]);
  const [positionTrades, setPositionTrades] = useState<PositionTrade[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [riskLimits, setRiskLimits] = useState<RiskLimit[]>([]);
  const [pendingChanges, setPendingChanges] = useState<PendingConfigChange[]>(
    [],
  );
  const [autoApprovalsConfig, setAutoApprovalsConfigState] =
    useState<AutoApprovalsConfig | null>(null);
  const [aiCostStatus, setAiCostStatus] = useState<AiCostStatusWire | null>(
    null,
  );
  const [systemHealth, setSystemHealth] = useState<SystemHealthRow[]>([]);
  const [recentEvents, setRecentEvents] = useState<EventRow[]>([]);
  const [cryptoSummary, setCryptoSummary] = useState<CryptoSummary | null>(
    null,
  );
  const [cryptoValidationRuns, setCryptoValidationRuns] = useState<
    CryptoValidationRun[]
  >([]);
  const [forexConfig, setForexConfig] = useState<ForexConfig | null>(null);
  const [forexPairs, setForexPairs] = useState<ForexPair[]>([]);
  const [forexAccount, setForexAccount] = useState<ForexAccount | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<Date | null>(null);
  const [degradedReads, setDegradedReads] = useState<string[]>([]);

  // Resolve the data source once on mount.
  useEffect(() => {
    let cancelled = false;
    void createDataSource().then((ds) => {
      if (!cancelled) setSource(ds);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (source === null) return;
    setError(null);
    const failedOptionalReads: string[] = [];
    try {
      const [a, p, ps, pr, pt, inv, bk, rl, pc, aa, acs, sh, re, cs, cvr, fxc, fxp, fxa] =
        await Promise.all([
          source.listAccounts(),
          source.listPending(),
          source.listPositions(),
          optionalRead(
            "position reassessments",
            () => source.listPositionReassessments({ limit: 100 }),
            { asOf: "", rows: [] as PositionReassessment[] },
            failedOptionalReads,
          ),
          optionalRead(
            "position trades",
            () => source.listPositionTrades(),
            [] as PositionTrade[],
            failedOptionalReads,
          ),
          optionalRead(
            "investments",
            () => source.listInvestments(),
            [] as Investment[],
            failedOptionalReads,
          ),
          optionalRead(
            "baskets",
            () => source.listBaskets(),
            [] as Basket[],
            failedOptionalReads,
          ),
          source.listRiskLimits(),
          source.listPendingConfigChanges(),
          source.getAutoApprovalsConfig(),
          optionalRead(
            "AI cost status",
            () => source.getAiCostStatus(),
            null as AiCostStatusWire | null,
            failedOptionalReads,
          ),
          source.listSystemHealth(),
          source.listRecentEvents(50),
          optionalRead(
            "crypto summary",
            () => source.getCryptoSummary(),
            null as CryptoSummary | null,
            failedOptionalReads,
          ),
          optionalRead(
            "crypto validation runs",
            () => source.listCryptoValidationRuns(5),
            [] as CryptoValidationRun[],
            failedOptionalReads,
          ),
          optionalRead(
            "forex config",
            () => source.getForexConfig(),
            null as ForexConfig | null,
            failedOptionalReads,
          ),
          optionalRead(
            "forex pairs",
            () => source.listForexPairs(),
            [] as ForexPair[],
            failedOptionalReads,
          ),
          optionalRead(
            "forex account",
            () => source.getForexAccount(),
            null as ForexAccount | null,
            failedOptionalReads,
          ),
        ]);
      setAccounts(a);
      setPending(p);
      setPositions(ps);
      setPositionReassessments(pr.rows);
      setPositionTrades(pt);
      setInvestments(inv);
      setBaskets(bk);
      setRiskLimits(rl);
      setPendingChanges(pc);
      setAutoApprovalsConfigState(aa);
      setAiCostStatus(acs);
      setSystemHealth(sh);
      setRecentEvents(re);
      setCryptoSummary(cs);
      setCryptoValidationRuns(cvr);
      setForexConfig(fxc);
      setForexPairs(fxp);
      setForexAccount(fxa);
      setLastSuccessAt(new Date());
      setDegradedReads(failedOptionalReads);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[useDataSource] refresh failed", e);
      // lastSuccessAt intentionally NOT cleared: the screens keep
      // showing the previous data, and the banner reports its age.
      setError(
        e instanceof Error ? e.message : "Failed to load dashboard data",
      );
    } finally {
      setIsLoading(false);
    }
  }, [source]);

  const generateSignals = useCallback(
    async (): Promise<GenerateSignalsResult> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.generateSignals();
    },
    [source],
  );

  const resumeBatchResearchEnrichment = useCallback<
    DataSource["resumeBatchResearchEnrichment"]
  >(
    async (workflowRunId) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.resumeBatchResearchEnrichment(workflowRunId);
    },
    [source],
  );

  const setAutoApprovalsConfig = useCallback(
    async (args: {
      enabled: boolean;
      minConfidence: number;
    }): Promise<AutoApprovalsConfig> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      const next = await source.setAutoApprovalsConfig(args);
      setAutoApprovalsConfigState(next);
      return next;
    },
    [source],
  );

  const listStrategyRoutes = useCallback<DataSource["listStrategyRoutes"]>(
    async () => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.listStrategyRoutes();
    },
    [source],
  );

  const updateStrategyRoute = useCallback<DataSource["updateStrategyRoute"]>(
    async (args) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.updateStrategyRoute(args);
    },
    [source],
  );

  const listRiskLimits = useCallback<DataSource["listRiskLimits"]>(
    async (brokerAccountId) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.listRiskLimits(brokerAccountId);
    },
    [source],
  );
  const getRiskSnapshot = useCallback<DataSource["getRiskSnapshot"]>(
    async (brokerAccountId) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.getRiskSnapshot(brokerAccountId);
    },
    [source],
  );

  const listCryptoValidationRuns = useCallback<
    DataSource["listCryptoValidationRuns"]
  >(async (limit) => {
    if (source === null) {
      throw new Error("Data source not yet initialised");
    }
    return source.listCryptoValidationRuns(limit);
  }, [source]);

  const listMoonshotSignals = useCallback<
    DataSource["listMoonshotSignals"]
  >(async (args) => {
    if (source === null) {
      throw new Error("Data source not yet initialised");
    }
    return source.listMoonshotSignals(args);
  }, [source]);

  const rankMoonshotTopTen = useCallback<
    DataSource["rankMoonshotTopTen"]
  >(async (args) => {
    if (source === null) {
      throw new Error("Data source not yet initialised");
    }
    return source.rankMoonshotTopTen(args);
  }, [source]);

  const getSectorLimits = useCallback<DataSource["getSectorLimits"]>(
    async (brokerAccountId) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.getSectorLimits(brokerAccountId);
    },
    [source],
  );

  const setSectorLimits = useCallback<DataSource["setSectorLimits"]>(
    async (args) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.setSectorLimits(args);
    },
    [source],
  );

  const repriceSignalApproval = useCallback(
    async (
      signal: GeneratedSignal,
      riskDollars?: string,
    ): Promise<string> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.repriceSignalApproval(signal, riskDollars);
    },
    [source],
  );

  const previewSignalApproval = useCallback(
    async (
      signal: GeneratedSignal,
      riskDollars?: string,
    ): Promise<{
      preview: TradePreview;
      actionable: boolean;
      issues: string[];
    }> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.previewSignalApproval(signal, riskDollars);
    },
    [source],
  );

  const listSignalHistory = useCallback(
    async (limit?: number): Promise<SignalHistoryRow[]> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.listSignalHistory(limit);
    },
    [source],
  );

  const listResearchReports = useCallback(
    async (limit?: number, symbol?: string): Promise<ResearchReportRow[]> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.listResearchReports(limit, symbol);
    },
    [source],
  );

  const createResearchReport = useCallback(
    async (args: {
      symbol: string;
      template?: "research_v1" | "deep_dive_v1";
      forceRegenerate?: boolean;
    }): Promise<ResearchReportCreateResult> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.createResearchReport(args);
    },
    [source],
  );

  const listSavedResearchEvaluations = useCallback<
    DataSource["listSavedResearchEvaluations"]
  >(
    async (limit?: number, symbol?: string) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.listSavedResearchEvaluations(limit, symbol);
    },
    [source],
  );

  const evaluateSymbol = useCallback(
    async (
      symbol: string,
      horizon: "day_swing" | "investment" | "both",
      asOf?: string,
    ): Promise<EvaluationResult> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.evaluateSymbol(symbol, horizon, asOf);
    },
    [source],
  );

  const acceptEvaluation = useCallback(
    async (evaluationId: string): Promise<void> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.acceptEvaluation(evaluationId);
    },
    [source],
  );

  const createApprovalFromEvaluation = useCallback(
    async (args: {
      evaluationId: string;
      brokerAccountId: string;
      sizedShares: string;
      accountEquitySnapshot: string;
    }): Promise<{ approvalId: string; symbol: string }> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.createApprovalFromEvaluation(args);
    },
    [source],
  );

  const runAgentReview = useCallback<DataSource["runAgentReview"]>(
    async (evaluationId, includeSocialSentiment, resumeWorkflowRunId) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.runAgentReview(
        evaluationId,
        includeSocialSentiment,
        resumeWorkflowRunId,
      );
    },
    [source],
  );

  const getAgentReview = useCallback<DataSource["getAgentReview"]>(
    async (evaluationId) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.getAgentReview(evaluationId);
    },
    [source],
  );

  const runShadowAgentReviews = useCallback<
    DataSource["runShadowAgentReviews"]
  >(
    async (args) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.runShadowAgentReviews(args);
    },
    [source],
  );

  const getWorkflowStatus = useCallback<DataSource["getWorkflowStatus"]>(
    async (workflowRunId) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.getWorkflowStatus(workflowRunId);
    },
    [source],
  );

  const getLatestWorkflowStatus = useCallback<
    DataSource["getLatestWorkflowStatus"]
  >(
    async (args) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.getLatestWorkflowStatus(args);
    },
    [source],
  );

  const listDecisionReflections = useCallback<
    DataSource["listDecisionReflections"]
  >(
    async (limit?: number, symbol?: string) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.listDecisionReflections(limit, symbol);
    },
    [source],
  );

  const generateDecisionReflections = useCallback<
    DataSource["generateDecisionReflections"]
  >(
    async (args) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.generateDecisionReflections(args);
    },
    [source],
  );

  const reviewApproval = useCallback(
    async (approvalId: string): Promise<ApprovalReviewResponse> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.reviewApproval(approvalId);
    },
    [source],
  );

  const resizeApproval = useCallback<DataSource["resizeApproval"]>(
    async (approvalId, riskDollars) => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.resizeApproval(approvalId, riskDollars);
    },
    [source],
  );

  const haltAll = useCallback(
    async (reason?: string): Promise<void> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.haltAll(reason);
    },
    [source],
  );

  const resumeAll = useCallback(
    async (): Promise<void> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.resumeAll();
    },
    [source],
  );

  const confirmAndPlaceApproval = useCallback(
    async (
      approvalId: string,
      idempotencyKey: string,
      liveRealMoneyAck = false,
    ): Promise<ConfirmAndPlaceResponse> => {
      if (source === null) {
        throw new Error("Data source not yet initialised");
      }
      return source.confirmAndPlaceApproval(
        approvalId,
        idempotencyKey,
        liveRealMoneyAck,
      );
    },
    [source],
  );

  const listChartSymbols = useCallback(async (): Promise<string[]> => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.listChartSymbols();
  }, [source]);

  const listClosedStockTrades = useCallback<DataSource["listClosedStockTrades"]>(
    async (args) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.listClosedStockTrades(args);
    },
    [source],
  );

  const listPositionReassessments = useCallback<
    DataSource["listPositionReassessments"]
  >(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.listPositionReassessments(args);
  }, [source]);

  const runPositionReassessment = useCallback<
    DataSource["runPositionReassessment"]
  >(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    const out = await source.runPositionReassessment(args);
    setPositionReassessments(out.rows);
    return out;
  }, [source]);

  const confirmPositionReassessmentAction = useCallback<
    DataSource["confirmPositionReassessmentAction"]
  >(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    const row = await source.confirmPositionReassessmentAction(args);
    setPositionReassessments((prev) =>
      prev.map((item) =>
        item.reassessmentId === row.reassessmentId ? row : item,
      ),
    );
    return row;
  }, [source]);

  const dismissPositionReassessment = useCallback<
    DataSource["dismissPositionReassessment"]
  >(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    const row = await source.dismissPositionReassessment(args);
    setPositionReassessments((prev) =>
      prev.filter((item) => item.reassessmentId !== row.reassessmentId),
    );
    return row;
  }, [source]);

  const getChartSnapshot = useCallback(
    async (
      symbol: string,
      lookback?: number,
      timeframe?: ChartTimeframe,
    ): Promise<ChartSnapshot> => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.getChartSnapshot(symbol, lookback, timeframe);
    },
    [source],
  );

  // Phase 5 (d) D2 pass-throughs.
  const getLivePnL = useCallback<DataSource["getLivePnL"]>(async () => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.getLivePnL();
  }, [source]);
  const tightenStop = useCallback<DataSource["tightenStop"]>(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.tightenStop(args);
  }, [source]);
  const adjustTarget = useCallback<DataSource["adjustTarget"]>(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.adjustTarget(args);
  }, [source]);
  const breakEven = useCallback<DataSource["breakEven"]>(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.breakEven(args);
  }, [source]);
  const exitAtMarket = useCallback<DataSource["exitAtMarket"]>(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.exitAtMarket(args);
  }, [source]);
  const exitAtLimit = useCallback<DataSource["exitAtLimit"]>(async (args) => {
    if (source === null) throw new Error("Data source not yet initialised");
    return source.exitAtLimit(args);
  }, [source]);
  const adjustPositionQty = useCallback<DataSource["adjustPositionQty"]>(
    async (args) => {
      if (source === null) throw new Error("Data source not yet initialised");
      return source.adjustPositionQty(args);
    },
    [source],
  );

  useEffect(() => {
    if (source === null) return;
    void refresh();
  }, [source, refresh]);

  // Memoize the returned object so consumers can key effects on `ds`
  // itself (e.g. DayTradesScreen's 1s chart poll) without the effect
  // re-firing — and the chart tearing down — on every unrelated App
  // re-render (2026-06-10 safety review, finding 11). All fields are
  // state values or useCallback-stable functions, so the dependency
  // list below is exhaustive and identity-stable between data changes.
  return useMemo(
    () => ({
    accounts,
    pending,
    positions,
    positionReassessments,
    positionTrades,
    investments,
    baskets,
    riskLimits,
    pendingChanges,
    autoApprovalsConfig,
    aiCostStatus,
    systemHealth,
    recentEvents,
    cryptoSummary,
    cryptoValidationRuns,
    forexConfig,
    forexPairs,
    forexAccount,
    isLoading,
    error,
    lastSuccessAt,
    degradedReads,
    refresh,
    generateSignals,
    resumeBatchResearchEnrichment,
    listCryptoValidationRuns,
    listMoonshotSignals,
    rankMoonshotTopTen,
    listPositionReassessments,
    runPositionReassessment,
    confirmPositionReassessmentAction,
    dismissPositionReassessment,
    listRiskLimits,
    getRiskSnapshot,
    getSectorLimits,
    setSectorLimits,
    setAutoApprovalsConfig,
    listStrategyRoutes,
    updateStrategyRoute,
    listSignalHistory,
    listResearchReports,
    createResearchReport,
    listSavedResearchEvaluations,
    evaluateSymbol,
    acceptEvaluation,
    createApprovalFromEvaluation,
    runAgentReview,
    getAgentReview,
    runShadowAgentReviews,
    getWorkflowStatus,
    getLatestWorkflowStatus,
    listDecisionReflections,
    generateDecisionReflections,
    repriceSignalApproval,
    previewSignalApproval,
    reviewApproval,
    resizeApproval,
    haltAll,
    resumeAll,
    confirmAndPlaceApproval,
    listClosedStockTrades,
    listChartSymbols,
    getChartSnapshot,
    getLivePnL,
    tightenStop,
    adjustTarget,
    breakEven,
    exitAtMarket,
    exitAtLimit,
    adjustPositionQty,
    usingFixtures: source?.usingFixtures ?? false,
    }),
    [
      accounts,
      pending,
      positions,
      positionReassessments,
      positionTrades,
      investments,
      baskets,
      riskLimits,
      pendingChanges,
      autoApprovalsConfig,
      aiCostStatus,
      systemHealth,
      recentEvents,
      cryptoSummary,
      cryptoValidationRuns,
      forexConfig,
      forexPairs,
      forexAccount,
      isLoading,
      error,
      lastSuccessAt,
      degradedReads,
      refresh,
      generateSignals,
      resumeBatchResearchEnrichment,
      listCryptoValidationRuns,
      listMoonshotSignals,
      rankMoonshotTopTen,
      listPositionReassessments,
      runPositionReassessment,
      confirmPositionReassessmentAction,
      dismissPositionReassessment,
      listRiskLimits,
      getRiskSnapshot,
      getSectorLimits,
      setSectorLimits,
      setAutoApprovalsConfig,
      listStrategyRoutes,
      updateStrategyRoute,
      listSignalHistory,
      listResearchReports,
      createResearchReport,
      listSavedResearchEvaluations,
      evaluateSymbol,
      acceptEvaluation,
      createApprovalFromEvaluation,
      runAgentReview,
      getAgentReview,
      runShadowAgentReviews,
      getWorkflowStatus,
      getLatestWorkflowStatus,
      listDecisionReflections,
      generateDecisionReflections,
      repriceSignalApproval,
      previewSignalApproval,
      reviewApproval,
      resizeApproval,
      haltAll,
      resumeAll,
      confirmAndPlaceApproval,
      listClosedStockTrades,
      listChartSymbols,
      getChartSnapshot,
      getLivePnL,
      tightenStop,
      adjustTarget,
      breakEven,
      exitAtMarket,
      exitAtLimit,
      adjustPositionQty,
      source,
    ],
  );
}
